import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { useParams, Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { useAdminProduct } from "@/hooks/useProducts";
import {
  deleteProductImage,
  formatOre,
  resolveProductImageUrl,
  updateProduct,
  updateVariant,
  uploadProductImage,
  type ImageRow,
  type VariantRow,
} from "@/lib/products";
import { handleApiError } from "@/lib/errors";
import { toast } from "@/components/ui/sonner";

interface ProductFormState {
  title: string;
  description: string;
  descriptionHtml: string;
  category: string;
  isActive: boolean;
}

const AdminProductDetail = () => {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { data, isLoading } = useAdminProduct(id);

  const [productForm, setProductForm] = useState<ProductFormState>({
    title: "",
    description: "",
    descriptionHtml: "",
    category: "",
    isActive: true,
  });
  const [savingProduct, setSavingProduct] = useState(false);
  const [variantDrafts, setVariantDrafts] = useState<Record<string, Partial<VariantRow>>>({});
  const [savingVariantId, setSavingVariantId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!data) return;
    setProductForm({
      title: data.product.title,
      description: data.product.description ?? "",
      descriptionHtml: data.product.description_html ?? "",
      category: data.product.category,
      isActive: data.product.is_active,
    });
    setVariantDrafts({});
  }, [data]);

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ["products"] });
  }

  async function handleSaveProduct(e: FormEvent) {
    e.preventDefault();
    if (!data) return;
    setSavingProduct(true);
    try {
      await updateProduct(data.product.id, {
        title: productForm.title.trim(),
        description: productForm.description.trim() || null,
        description_html: productForm.descriptionHtml.trim() || null,
        category: productForm.category.trim(),
        is_active: productForm.isActive,
      });
      toast.success("Produktet er oppdatert.");
      invalidate();
    } catch (err) {
      handleApiError(err, "Kunne ikke lagre produktet.");
    } finally {
      setSavingProduct(false);
    }
  }

  function setVariantField<K extends keyof VariantRow>(
    variantId: string,
    field: K,
    value: VariantRow[K]
  ) {
    setVariantDrafts((d) => ({ ...d, [variantId]: { ...d[variantId], [field]: value } }));
  }

  async function saveVariant(variant: VariantRow) {
    const draft = variantDrafts[variant.id];
    if (!draft) return;
    setSavingVariantId(variant.id);
    try {
      await updateVariant(variant.id, draft);
      toast.success("Variant lagret.");
      setVariantDrafts((d) => {
        const next = { ...d };
        delete next[variant.id];
        return next;
      });
      invalidate();
    } catch (err) {
      handleApiError(err, "Kunne ikke lagre varianten.");
    } finally {
      setSavingVariantId(null);
    }
  }

  async function handleUpload(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !data) return;
    setUploading(true);
    try {
      await uploadProductImage(data.product.id, file);
      toast.success("Bilde lastet opp.");
      invalidate();
    } catch (err) {
      handleApiError(err, "Kunne ikke laste opp bilde.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  async function handleDeleteImage(image: ImageRow) {
    if (!confirm("Slette dette bildet?")) return;
    try {
      await deleteProductImage(image);
      toast.success("Bilde slettet.");
      invalidate();
    } catch (err) {
      handleApiError(err, "Kunne ikke slette bildet.");
    }
  }

  if (isLoading) return <p className="text-sm text-muted-foreground">Laster …</p>;
  if (!data) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-destructive">Fant ikke produktet.</p>
        <Button variant="ghost" asChild>
          <Link to="/admin/products">Tilbake</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-8">
      <div>
        <Button variant="ghost" size="sm" asChild className="-ml-3">
          <Link to="/admin/products">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Tilbake
          </Link>
        </Button>
        <h1 className="mt-2 text-3xl font-semibold">{data.product.title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Slug: <span className="font-mono">{data.product.slug}</span>
        </p>
      </div>

      <form onSubmit={handleSaveProduct} className="space-y-5 rounded-lg border bg-card p-6">
        <div className="space-y-2">
          <Label htmlFor="title">Tittel</Label>
          <Input
            id="title"
            value={productForm.title}
            onChange={(e) => setProductForm({ ...productForm, title: e.target.value })}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="category">Kategori</Label>
          <Input
            id="category"
            value={productForm.category}
            onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
          />
          <p className="text-xs text-muted-foreground">
            f.eks. mennesker, hunder. Rekkefølgen mellom produkter styres fra produkt-listen.
          </p>
        </div>

        <div className="space-y-2">
          <Label>Beskrivelse</Label>
          <RichTextEditor
            value={productForm.descriptionHtml}
            onChange={(html, plain) =>
              setProductForm((f) => ({ ...f, descriptionHtml: html, description: plain }))
            }
            placeholder="Skriv produktbeskrivelse, lag overskrifter, lister og fremhevet tekst …"
          />
          <p className="text-xs text-muted-foreground">
            Bruk verktøyene over for overskrifter, lister og uthevet tekst. Innholdet vises som strukturert tekst på produktsiden.
          </p>
        </div>

        <div className="flex items-center gap-3 rounded-md border p-4">
          <Switch
            id="isActive"
            checked={productForm.isActive}
            onCheckedChange={(v) => setProductForm({ ...productForm, isActive: v })}
          />
          <div>
            <Label htmlFor="isActive" className="cursor-pointer">
              Aktiv i butikken
            </Label>
            <p className="text-xs text-muted-foreground">
              Skjules fra butikkfeed og produktside hvis slått av.
            </p>
          </div>
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={savingProduct}>
            {savingProduct ? "Lagrer …" : "Lagre produkt"}
          </Button>
        </div>
      </form>

      <Separator />

      <div>
        <h2 className="mb-3 text-lg font-semibold">
          {data.variants.length > 1 ? "Varianter" : "Pris og lager"}
        </h2>
        <div className="space-y-3">
          {data.variants.map((v) => {
            const draft = variantDrafts[v.id] ?? {};
            const dirty = Object.keys(draft).length > 0;
            const merged = { ...v, ...draft };
            const showVariantTitle = data.variants.length > 1;
            return (
              <div key={v.id} className="rounded-md border bg-card p-4">
                {showVariantTitle && (
                  <p className="mb-3 text-sm font-medium">{merged.title}</p>
                )}
                <div className="grid gap-3 md:grid-cols-[160px_140px_auto_auto] md:items-end">
                  <div className="space-y-1">
                    <Label className="text-xs">Pris (NOK, inkl. mva)</Label>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={(merged.price_ore / 100).toString()}
                      onChange={(e) =>
                        setVariantField(v.id, "price_ore", Math.round(Number(e.target.value) * 100))
                      }
                    />
                    <p className="text-[10px] text-muted-foreground">{formatOre(merged.price_ore)}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Lager</Label>
                    <Input
                      type="number"
                      min={0}
                      value={merged.inventory?.toString() ?? ""}
                      placeholder="Ubegrenset"
                      onChange={(e) =>
                        setVariantField(
                          v.id,
                          "inventory",
                          e.target.value === "" ? null : Number(e.target.value)
                        )
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">SKU</Label>
                    <p className="rounded-md bg-muted/40 px-3 py-2 text-sm font-mono text-muted-foreground">
                      {merged.sku ?? "—"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 pb-1">
                    <Switch
                      checked={merged.is_active}
                      onCheckedChange={(checked) => setVariantField(v.id, "is_active", checked)}
                    />
                    <span className="text-xs text-muted-foreground">
                      {merged.is_active ? "Aktiv" : "Skjult"}
                    </span>
                  </div>
                </div>
                {dirty && (
                  <div className="mt-3 flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setVariantDrafts((d) => {
                          const next = { ...d };
                          delete next[v.id];
                          return next;
                        })
                      }
                    >
                      Avbryt
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => void saveVariant(v)}
                      disabled={savingVariantId === v.id}
                    >
                      {savingVariantId === v.id ? "Lagrer …" : "Lagre"}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <Separator />

      <div>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Bilder</h2>
          <Label className="cursor-pointer">
            <input
              type="file"
              accept="image/*"
              onChange={handleUpload}
              className="hidden"
              disabled={uploading}
            />
            <span className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90">
              <Upload className="h-4 w-4" />
              {uploading ? "Laster opp …" : "Last opp bilde"}
            </span>
          </Label>
        </div>
        {data.images.length === 0 ? (
          <p className="text-sm text-muted-foreground">Ingen bilder. Last opp ett for å vise i butikken.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {data.images.map((img) => {
              const url = resolveProductImageUrl(img.storage_path);
              return (
                <div key={img.id} className="relative overflow-hidden rounded-md border bg-card">
                  {url ? (
                    <img src={url} alt={img.alt ?? ""} className="aspect-square w-full object-cover" />
                  ) : (
                    <div className="flex aspect-square items-center justify-center text-xs text-muted-foreground">
                      Ingen URL
                    </div>
                  )}
                  <div className="flex items-center justify-between bg-card/90 p-2 text-xs text-muted-foreground">
                    <span>#{img.sort_order}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void handleDeleteImage(img)}
                      className="h-7 px-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProductDetail;
